import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { cloneDeep, every, filter, includes, some, values } from 'lodash';
import { Observable, Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { ICollegePath } from 'src/ng2/shared/typings/interfaces/paths.interface';
import { getCollegePathLoadedStatus, getCollegePathsEntitiesList } from 'Src/ng2/store';
import { SpinnerModal } from '../../../../app/shared/modals/status-spinner/status-spinner.service';
import { IACOption } from '../../../../nvps-libraries/design/nv-textbox/nv-textbox.interface';
import { IDropdownOption } from '../../typings/interfaces/design-library.interface';
import { IStudentPath } from '../../typings/interfaces/studentPaths.interface';
import { TValidStudentPathStatuses } from '../../typings/types/student-path.types';
import { BaseModalComponent } from '../base-modal.component';
import { IBaseModalData } from '../modals.service';
import { LoadCollegePath } from './../../../store/actions/college-path-actions';
import { COLLEGE_PATH_STATUSES, STUDENT_PATH_FINAL_DECISION_STATUSES } from './../../constants/student-paths.constant';
import { STUDENT_PATHS_MODAL_CONFIG } from './student-paths-modal.config';

export interface IStudentPathsModalComponentData extends IBaseModalData {
  schoolId: string;
  studentIds: string[];
  mode: string;
  studentPaths?: IStudentPath[];
}

/* istanbul ignore next */
@Component({
  selector: 'student-paths-modal',
  template: require('./student-paths-modal.component.html'),
  styles: [require('./student-paths-modal.component.scss')],
})
export class StudentPathsModalComponent extends BaseModalComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  public collegePaths: ICollegePath[];
  public placeholder: string;
  public placeholderIsEnabled: boolean = true;
  public optionIsSelected: boolean = false;
  public count: number;
  // Modal Configurations
  readonly buttons = STUDENT_PATHS_MODAL_CONFIG.buttons;

  public destroy$: Subject<boolean> = new Subject<boolean>();

  collegeNameSubscription: ISubscription;

  public itemCount: number;
  public itemType: string;
  public title: string;
  public confirmationButtonLabel: string;
  private collegePathsLoaded = false;
  public isEditMode: boolean;
  public validStatuses: string[];
  public statusSelection: string;
  public showWarning: boolean;
  public warningText: string = 'This action will set the final decision to the selected college';
  public notePriority: string = 'warning';

  // college search configs
  public moreOptionsText: string;
  public emptyStateText: string;
  private matchedColleges: IACOption[];
  public filteredColleges: IACOption[];
  public colleges: IACOption[];
  public collegePathsMap: Map<string, ICollegePath>;
  public selectedCollege: ICollegePath;
  private studentPathsMap: Map<string, IStudentPath>;

  // dropdown configs
  public statusOptions: IDropdownOption[];
  public selectedStatus: IDropdownOption;
  public statusOptionsMap: Map<string, IDropdownOption>;

  constructor(
    dialogRef: MatDialogRef<StudentPathsModalComponent>,
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: IStudentPathsModalComponentData,
    @Inject('SpinnerModal') private spinnerModal: SpinnerModal,
    @Inject('Rollbar') private rollbar,
    private store: Store<any>,
  ) {
    super(dialogRef);
  }

  public ngOnInit(): void {
    // clone data to avoid mutating the passed in data
    const { mode, studentIds, isProfileMode, studentPaths } = (this.data = cloneDeep(this.data));
    this.itemCount = studentIds.length;
    this.itemType = 'student';
    this.isEditMode = mode === 'UPDATE';
    this.isProfileMode = isProfileMode;
    if (isProfileMode)
      this.studentPathsMap = new Map(studentPaths.map(studentPath => [studentPath.path.pathId, studentPath]) as [
        string,
        IStudentPath
      ][]);
    this.validStatuses = [
      COLLEGE_PATH_STATUSES.PLANS_TO_APPLY,
      COLLEGE_PATH_STATUSES.APPLIED,
      COLLEGE_PATH_STATUSES.APPLIED_WITH_SPECIAL_OPP,
      COLLEGE_PATH_STATUSES.ACCEPTED,
      COLLEGE_PATH_STATUSES.ACCEPTED_WITH_SPECIAL_OPP,
      COLLEGE_PATH_STATUSES.WAITLISTED,
      COLLEGE_PATH_STATUSES.DENIED,
      COLLEGE_PATH_STATUSES.COMMITTED,
      COLLEGE_PATH_STATUSES.COMMITTED_WITH_SPECIAL_OPP,
    ];
    this.statusOptions = [];
    this.statusOptionsMap = new Map();
    this.validStatuses.forEach(status => {
      let option = { key: status, human: status };
      this.statusOptions.push(option);
      this.statusOptionsMap.set(status, option);
    });
    this.selectedStatus = this.statusOptions[0]; // defaults to plans to apply
    this.showWarning = false;

    this.store
      .select(getCollegePathLoadedStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe(loaded => {
        if (!loaded) {
          const payload = { schoolId: this.data.schoolId, pathType: 'COLLEGE' };
          this.store.dispatch(new LoadCollegePath(payload));
        } else {
          this.store
            .select(getCollegePathsEntitiesList)
            .pipe(takeUntil(this.destroy$))
            .subscribe(collegePaths => {
              this.collegePaths = collegePaths;
              this.collegePathsLoaded = true;

              // TODO: revert key back to updated college/campus Id + college/stu pathsmap
              // map college paths to be in IACOption Format
              this.colleges = this.collegePaths
                .map(collegePath => {
                  const { _id, collegeId, name, city, state } = collegePath;
                  return {
                    key: _id,
                    human: name,
                    tags: [{ key: city, human: city }, { key: state, human: state }],
                  };
                })
                .sort((a, b) => (a.human > b.human ? 1 : -1));

              this.collegePathsMap = new Map(this.collegePaths.map(collegePath => [collegePath._id, collegePath]) as [
                string,
                ICollegePath
              ][]);
              this.filteredColleges = [];
            });
        }
      });

    // update modal UI
    if (mode === 'ADD') {
      this.title = 'Add College';
      this.confirmationButtonLabel = 'Add College';
    } else if (mode === 'UPDATE') {
      this.title = 'Update Status';
      this.confirmationButtonLabel = 'Update Status';
    } else if (mode === 'REMOVE') {
      this.title = 'Remove College';
      this.confirmationButtonLabel = 'Remove College';
    }
    // build form
    this.form = this.buildForm();

    // get placeholder value
    this.placeholder = STUDENT_PATHS_MODAL_CONFIG.input.placeholder;

    // subscription listens for changes in input to update autocomplete
    this._subscribeToCollgeNameValueChanges(this.form);
  }

  public close(): void {
    super.close();
  }

  public submit(form: FormGroup): void {
    const patch = { pathId: this.selectedCollege._id, status: this.selectedStatus.human };
    super.close(patch);
  }

  public _subscribeToCollgeNameValueChanges(form: FormGroup) {
    const collegeNameController = form.controls.collegeNameSelection;

    this.collegeNameSubscription = collegeNameController.valueChanges.subscribe(value => {
      if (this.optionIsSelected && value !== this.selectedCollege.name) {
        this.selectedCollege = null;
        this.optionIsSelected = !!this.selectedCollege;
      }
      if (typeof value === 'string') this._filterColleges(value);
    });
  }

  public buildForm(): FormGroup {
    const form = this.formBuilder.group({
      collegeNameSelection: [null, Validators.required],
    });

    return form;
  }

  public clearCollegeName(): void {
    this.form.controls.collegeNameSelection.setValue(null);
    this.filteredColleges = null;
    this.selectedCollege = null;
    this._setMoreOptionsText(this.matchedColleges, this.filteredColleges);
  }

  public selectCollegeName(event): void {
    this.form.controls.collegeNameSelection.setValue(event.human);
    const college = this.collegePathsMap.get(event.key);
    this.selectedCollege = college;
    this.optionIsSelected = !!this.selectedCollege;
    this.matchedColleges = null;
    this.filteredColleges = null;
    this._setMoreOptionsText(this.matchedColleges, this.filteredColleges);
  }

  public selectStatus(optionKey: IDropdownOption['key']): void {
    this.selectedStatus = this.statusOptionsMap.get(optionKey);
    const decisionStatuses = [
      STUDENT_PATH_FINAL_DECISION_STATUSES.COMMITTED,
      STUDENT_PATH_FINAL_DECISION_STATUSES.COMMITTED_WITH_SPECIAL_OPP,
    ];
    this.showWarning = includes(decisionStatuses, optionKey);
  }

  public _filterColleges(filterInput: string): void {
    if (!filterInput || filterInput === '') {
      this.matchedColleges = null;
      this.filteredColleges = null;
    } else {
      let lowerFilter: string[];
      lowerFilter = filterInput.toLowerCase().split(/[\s,]+/);

      // filter all of the colleges by the input
      this.matchedColleges = filter(this.colleges, college => {
        const { key, human, tags } = college;

        // Only in student profile, check to see if studentPath already exist
        // if it does do not display it
        if (this.isProfileMode && this.studentPathsMap.get(key)) return false;

        const city = tags[0].human;
        const state = tags[1].human;
        const collegeType = this.collegePathsMap.get(key).collegeType;
        const fullCollege = `${human} ${state} ${city} ${collegeType}`.toLowerCase();

        return every(lowerFilter, val => fullCollege.includes(val));
      });
      this.filteredColleges = this.matchedColleges.slice(0, 75);
    }
    this._setMoreOptionsText(this.matchedColleges, this.filteredColleges);
  }

  private _setMoreOptionsText(matched: IACOption[], filtered: IACOption[]) {
    if (!filtered) {
      this.moreOptionsText = null;
      this.emptyStateText = null;
    } else {
      const diffCount = matched.length - filtered.length;
      if (diffCount > 0) {
        this.moreOptionsText = `${diffCount} more results. Refine your search.`;
        this.emptyStateText = null;
      } else {
        this.moreOptionsText = null;
        this.emptyStateText = 'No colleges found';
      }
    }
  }

  public canSubmit(): boolean {
    return this.optionIsSelected && !!this.selectedStatus;
  }
}
